import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// ── הגדרת שלבי ה-Walkthrough ──
const STEPS = [
  {
    target: '.home-extract-cta',
    route: '/',
    title: 'ברוכים הבאים! 🍳',
    content: 'לחצו כאן כדי להתחיל לחלץ מתכון חדש מסרטון, טקסט או תמונה.',
    placement: 'bottom',
  },
  {
    target: '.url-input',
    route: '/add',
    title: 'מתחילים כאן ✍️',
    content: 'מצאתם מתכון? העתיקו לינק מאינסטגרם, טיקטוק או פייסבוק והדביקו אותו כאן.',
    placement: 'bottom',
  },
  {
    target: '.extract-btn',
    route: '/add',
    title: 'הוקוס פוקוס 🪄',
    content: 'לחצו על הכפתור ותנו ל-AI לחלץ עבורכם את המתכון המלא תוך שניות.',
    placement: 'top',
  },
];

const PAD = 10; // ריפוד סביב האלמנט המודגש
const TOOLTIP_W = 300;

const Walkthrough = () => {
  const [showWelcome, setShowWelcome] = useState(false);
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);        // מיקום האלמנט ב-DOM
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

  const navigate = useNavigate();
  const location = useLocation();

  // הצג Welcome Modal אם עדיין לא הושלם
  useEffect(() => {
    const done = localStorage.getItem('cookswithai_walkthrough_completed');
    if (!done) setShowWelcome(true);
  }, []);

  // ── מצא ועדכן מיקום האלמנט בכל פעם שהשלב משתנה או הדף נטען ──
  const updateRect = useCallback(() => {
    const step = STEPS[stepIndex];
    if (!step || !active) return;

    const el = document.querySelector(step.target);
    if (!el) return;

    el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // קצת השהיה כדי לאפשר ל-scroll להסתיים
    setTimeout(() => {
      const r = el.getBoundingClientRect();
      setRect(r);

      // חישוב מיקום ה-Tooltip
      let top, left;

      if (step.placement === 'bottom') {
        top = r.bottom + PAD + 12;
      } else {
        top = r.top - 12; // ייקבע לפי גובה ה-tooltip
      }

      left = r.left + r.width / 2 - TOOLTIP_W / 2;
      // שמור בתוך מסך
      left = Math.max(12, Math.min(left, window.innerWidth - TOOLTIP_W - 12));
      top = Math.max(12, top);

      setTooltipPos({ top, left, placement: step.placement, elBottom: r.bottom, elTop: r.top });
    }, 100);
  }, [active, stepIndex]);

  useEffect(() => {
    if (!active) return;

    // הגדרת delay כדי לתת לדף להתרנדר לאחר ניווט
    const timer = setTimeout(updateRect, 350);
    window.addEventListener('resize', updateRect);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateRect);
    };
  }, [active, stepIndex, location.pathname, updateRect]);

  // ── פעולות ──
  const startTour = () => {
    setShowWelcome(false);
    setStepIndex(0);
    setRect(null);
    // ניווט לדף הראשון אם צריך
    if (location.pathname !== STEPS[0].route) {
      navigate(STEPS[0].route);
    }
    setActive(true);
  };

  const skipTour = () => {
    setShowWelcome(false);
    localStorage.setItem('cookswithai_walkthrough_completed', 'true');
  };

  const finishTour = () => {
    setActive(false);
    setRect(null);
    localStorage.setItem('cookswithai_walkthrough_completed', 'true');
    navigate('/');
  };

  const goNext = () => {
    const nextIdx = stepIndex + 1;

    if (nextIdx >= STEPS.length) {
      finishTour();
      return;
    }

    const nextStep = STEPS[nextIdx];
    const currStep = STEPS[stepIndex];

    setRect(null); // נקה בזמן מעבר
    setStepIndex(nextIdx);

    if (nextStep.route !== currStep.route) {
      navigate(nextStep.route);
    }
  };

  const goPrev = () => {
    const prevIdx = stepIndex - 1;
    if (prevIdx < 0) return;

    const prevStep = STEPS[prevIdx];
    const currStep = STEPS[stepIndex];

    setRect(null);
    setStepIndex(prevIdx);

    if (prevStep.route !== currStep.route) {
      navigate(prevStep.route);
    }
  };

  const step = STEPS[stepIndex];

  // ── חישוב מיקום ה-tooltip כשהוא מעל (placement=top) ──
  // נחשב אחרי שניצור את ה-tooltip עם גובה קבוע של ~160px
  const tooltipTop = tooltipPos.placement === 'top'
    ? (tooltipPos.elTop ?? 0) - 160 - PAD - 12
    : tooltipPos.top;

  return (
    <>
      {/* ── Welcome Modal ── */}
      {showWelcome && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 10001,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          }}
        >
          <div
            style={{
              background: '#fff', width: '100%', maxWidth: 360,
              borderRadius: 32, padding: 32, textAlign: 'center',
              boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
              direction: 'rtl',
            }}
          >
            <div style={{
              width: 72, height: 72, borderRadius: '50%',
              background: 'rgba(0,106,98,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 36, color: '#006a62', fontVariationSettings: "'FILL' 1" }}>
                auto_awesome
              </span>
            </div>

            <h2 style={{ fontSize: 22, fontWeight: 900, marginBottom: 12, color: '#1a1c1c' }}>
              נעים להכיר, שף! 🧑‍🍳
            </h2>
            <p style={{ color: '#666', lineHeight: 1.6, marginBottom: 28 }}>
              ברוכים הבאים ל-CooksWithAI.<br />
              רוצים סיור קצר של שניות כדי לראות איך להפוך סרטון למתכון?
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={startTour}
                style={{
                  padding: '14px 0', background: '#006a62', color: '#fff',
                  border: 'none', borderRadius: 999, fontWeight: 700,
                  fontSize: 16, cursor: 'pointer',
                }}
              >
                בטח, קח אותי לסיור! 🚀
              </button>
              <button
                onClick={skipTour}
                style={{
                  padding: '12px 0', background: 'transparent', color: '#888',
                  border: 'none', borderRadius: 999, fontWeight: 600,
                  fontSize: 14, cursor: 'pointer',
                }}
              >
                לא תודה, אני כבר מסתדר
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Walkthrough Overlay ── */}
      {active && rect && (
        <>
          {/* ── Spotlight – 4 מלבנים שחורים סביב האלמנט ── */}
          {/* למעלה */}
          <div style={{
            position: 'fixed', zIndex: 9998, pointerEvents: 'none',
            top: 0, left: 0, right: 0,
            height: Math.max(0, rect.top - PAD),
            background: 'rgba(0,0,0,0.55)',
          }} />
          {/* למטה */}
          <div style={{
            position: 'fixed', zIndex: 9998, pointerEvents: 'none',
            top: rect.bottom + PAD, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.55)',
          }} />
          {/* שמאל */}
          <div style={{
            position: 'fixed', zIndex: 9998, pointerEvents: 'none',
            top: rect.top - PAD, left: 0,
            width: Math.max(0, rect.left - PAD),
            height: rect.height + PAD * 2,
            background: 'rgba(0,0,0,0.55)',
          }} />
          {/* ימין */}
          <div style={{
            position: 'fixed', zIndex: 9998, pointerEvents: 'none',
            top: rect.top - PAD,
            left: rect.right + PAD, right: 0,
            height: rect.height + PAD * 2,
            background: 'rgba(0,0,0,0.55)',
          }} />

          {/* מסגרת spotlight */}
          <div style={{
            position: 'fixed', zIndex: 9999, pointerEvents: 'none',
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            border: '2px solid #006a62',
            borderRadius: 12,
            boxShadow: '0 0 0 4px rgba(0,106,98,0.25)',
          }} />

          {/* ── Tooltip ── */}
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'fixed',
              zIndex: 10000,
              top: Math.max(12, tooltipTop),
              left: tooltipPos.left,
              width: TOOLTIP_W,
              background: '#fff',
              borderRadius: 18,
              padding: '20px 22px 16px',
              boxShadow: '0 16px 40px rgba(0,0,0,0.22)',
              direction: 'rtl',
            }}
          >
            {/* כותרת */}
            <p style={{ fontWeight: 800, fontSize: 16, color: '#1a1c1c', marginBottom: 8 }}>
              {step.title}
            </p>
            {/* תוכן */}
            <p style={{ fontSize: 14, color: '#555', lineHeight: 1.6, marginBottom: 16 }}>
              {step.content}
            </p>

            {/* כפתורים + progress */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              {/* חזור / דלג */}
              <button
                onClick={stepIndex === 0 ? finishTour : goPrev}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#999', fontSize: 13, fontWeight: 600, padding: '4px 0',
                }}
              >
                {stepIndex === 0 ? 'דלג' : 'חזור'}
              </button>

              {/* נקודות progress */}
              <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                {STEPS.map((_, i) => (
                  <div
                    key={i}
                    style={{
                      width: i === stepIndex ? 20 : 7,
                      height: 7, borderRadius: 999,
                      background: i === stepIndex ? '#006a62' : '#ddd',
                      transition: 'all 0.3s',
                    }}
                  />
                ))}
              </div>

              {/* הבא / הבנתי */}
              <button
                onClick={goNext}
                style={{
                  background: '#006a62', color: '#fff',
                  border: 'none', borderRadius: 999,
                  padding: '8px 18px', fontWeight: 700,
                  fontSize: 14, cursor: 'pointer',
                }}
              >
                {stepIndex === STEPS.length - 1 ? 'הבנתי! 🎉' : 'הבא →'}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Walkthrough;
